import type { ChangeEvent, DragEvent } from 'react';
import { useCallback, useRef, useState, useEffect } from 'react';
import { Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useApiToken } from '@/auth/hooks';
import { DocumentListItem } from '@/components/document/DocumentListItem';
import { useWorkspaceHub, type DocumentListChangedEvent } from '@/hooks/useWorkspaceHub';
import { logger } from '@/lib/logger';
import { useTriageStore, uploadSourceKey } from '@/stores/useTriageStore';
import { ConnectorTriageBatchDialog } from '@/features/connectors/ConnectorTriageBatchDialog';
import {
  getOrCreateHubConnection,
  ensureHubConnected,
  incrementSubscribers,
  decrementAndMaybeDisconnect,
} from '@/hooks/hubConnection';

const MAX_FILE_SIZE_MB = 250;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.docx', '.md'];
const ACCEPTED_ACCEPT = ACCEPTED_EXTENSIONS.join(',');

export function SourcePanel({
  defaultExpanded = true,
  selectedDocumentId,
  onSelectDocument,
}: {
  defaultExpanded?: boolean;
  selectedDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastTokenRef = useRef<string | null>(null);

  const documents = useDocumentStore((s) => s.documents);
  const isUploading = useDocumentStore((s) => s.isUploading);
  const fetchDocuments = useDocumentStore((s) => s.fetchDocuments);
  const uploadDocument = useDocumentStore((s) => s.uploadDocument);
  const deleteDocument = useDocumentStore((s) => s.deleteDocument);
  const setDocumentStatus = useDocumentStore((s) => s.setDocumentStatus);
  const fetchProcessingLogs = useDocumentStore((s) => s.fetchProcessingLogs);
  const { activeWorkspace } = useWorkspaceStore();
  const { getAccessToken } = useApiToken();

  // Upload triage state
  const uploadKey = activeWorkspace?.id ? uploadSourceKey(activeWorkspace.id) : '';
  const uploadTriageStatus = useTriageStore((s) => s.status[uploadKey] || 'idle');
  const pruneUploadTriageResults = useTriageStore((s) => s.pruneTriageResults);
  const [triageDialogOpen, setTriageDialogOpen] = useState(false);

  // Auto-open dialog when triage results arrive
  useEffect(() => {
    if (uploadTriageStatus === 'awaiting_confirmation' || uploadTriageStatus === 'running') {
      setTriageDialogOpen(true);
    }
  }, [uploadTriageStatus]);

  useEffect(() => {
    if (!activeWorkspace?.id || !uploadKey) return;

    const remaining = pruneUploadTriageResults(
      uploadKey,
      documents.map((document) => document.id),
    );

    if (remaining === 0 && triageDialogOpen) {
      setTriageDialogOpen(false);
    }
  }, [activeWorkspace?.id, documents, pruneUploadTriageResults, triageDialogOpen, uploadKey]);

  // Register upload-triage SignalR listeners early (before dialog mounts)
  // so events arriving during the race between trigger and dialog mount aren't lost.
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    const wsId = activeWorkspace.id;
    const key = uploadSourceKey(wsId);

    let cancelled = false;
    const connection = getOrCreateHubConnection();
    incrementSubscribers();

    const onStarted = (data: { workspaceId: string }) => {
      if (cancelled || data.workspaceId !== wsId) return;
      useTriageStore.getState().setTriageStatus(key, 'running');
    };
    const onProgress = (data: { workspaceId: string; processed: number; total: number; currentFile: string }) => {
      if (cancelled || data.workspaceId !== wsId) return;
      useTriageStore.getState().setTriageProgress(key, data.processed, data.total, data.currentFile ?? '');
    };
    const onResults = (data: { workspaceId: string; results: import('@/stores/useTriageStore').TriageResultDto[]; total: number }) => {
      if (cancelled || data.workspaceId !== wsId) return;
      useTriageStore.getState().appendTriageResults(key, data.results);
      useTriageStore.getState().setTriageStatus(key, 'awaiting_confirmation');
    };

    connection.on('UploadTriageStarted', onStarted);
    connection.on('UploadTriageProgress', onProgress);
    connection.on('UploadTriageResults', onResults);

    ensureHubConnected(connection).catch((err) => {
      if (!cancelled) logger.error('SourcePanel', 'Hub connection error', err);
    });

    return () => {
      cancelled = true;
      connection.off('UploadTriageStarted', onStarted);
      connection.off('UploadTriageProgress', onProgress);
      connection.off('UploadTriageResults', onResults);
      decrementAndMaybeDisconnect();
    };
  }, [activeWorkspace?.id]);

  // Fetch documents when workspace changes
  useEffect(() => {
    if (activeWorkspace?.id) {
      getAccessToken().then((token) => {
        if (token) {
          fetchDocuments(activeWorkspace.id, token);
        }
      });
    }
  }, [activeWorkspace?.id, getAccessToken, fetchDocuments]);

  // Subscribe to workspace-level SignalR events for cross-browser sync.
  // When another browser uploads/deletes a document or a document status changes,
  // we automatically refresh the document list.
  useWorkspaceHub(
    activeWorkspace?.id ?? null,
    {
      onDocumentListChanged: useCallback((event: DocumentListChangedEvent) => {
        logger.debug('SourcePanel', 'DocumentListChanged', { changeType: event.changeType, documentId: event.documentId });
        if (activeWorkspace?.id) {
          getAccessToken().then((token) => {
            if (token) {
              fetchDocuments(activeWorkspace.id, token);
            }
          });
        }
      }, [activeWorkspace?.id, getAccessToken, fetchDocuments]),
    },
  );

  // Monitor document status for toast notifications
  useEffect(() => {
    documents.forEach((doc) => {
      if (doc.status === 'Completed' && doc.completedAt) {
        // Check if we already showed this toast (within last 5 seconds)
        const completedTime = new Date(doc.completedAt).getTime();
        const now = Date.now();
        if (now - completedTime < 5000) {
          toast.success(`Dokument "${doc.fileName}" erfolgreich verarbeitet`, {
            description: `${doc.processedChunks} Chunks extrahiert`,
          });
        }
      } else if (doc.status === 'Failed' && doc.errorMessage) {
        toast.error(`Fehler bei "${doc.fileName}"`, {
          description: doc.errorMessage,
          action: {
            label: 'Erneut versuchen',
            onClick: () => {
              // TODO: Implement retry logic when Python API is ready
              logger.debug('SourcePanel', 'Retry requested', doc.id);
            },
          },
        });
      }
    });
  }, [documents]);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!activeWorkspace) {
        toast.error('Kein Workspace ausgewählt');
        return;
      }

      const token = await getAccessToken().catch(() => null);
      if (!token) {
        toast.error('Authentifizierung erforderlich');
        return;
      }
      lastTokenRef.current = token;

      const validFiles = files.filter(f =>
        ACCEPTED_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext))
      );
      if (validFiles.length === 0) {
        toast.error('Ungültiges Dateiformat', {
          description: `Unterstützte Formate: ${ACCEPTED_EXTENSIONS.join(', ')}`,
        });
        return;
      }

      for (const file of validFiles) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`Datei zu groß: "${file.name}"`, {
            description: `Maximale Dateigröße: ${MAX_FILE_SIZE_MB} MB (Datei: ${(file.size / 1024 / 1024).toFixed(1)} MB)`,
          });
          continue;
        }
        const result = await uploadDocument(
          file,
          activeWorkspace.id,
          activeWorkspace.domain.id,
          token
        );

        if (result) {
          toast.success(`"${file.name}" hochgeladen`);
        } else {
          toast.error(`Upload fehlgeschlagen: ${file.name}`);
        }
      }
    },
    [activeWorkspace, getAccessToken, uploadDocument]
  );

  const handleFileInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      if (files.length > 0) {
        handleFiles(files);
      }
      event.target.value = '';
    },
    [handleFiles]
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleDelete = useCallback(
    async (id: string) => {
      const token = await getAccessToken().catch(() => null);
      if (!token) {
        toast.error('Authentifizierung erforderlich');
        return;
      }
      
      try {
        await deleteDocument(id, token);
        toast.success('Dokument gelöscht');
      } catch (error) {
        toast.error('Löschen fehlgeschlagen');
      }
    },
    [deleteDocument, getAccessToken]
  );

  return (
    <div className="flex min-h-0 flex-col rounded-lg border border-border bg-card/60">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full shrink-0 items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Quellen
          {documents.length > 0 && (
            <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/10 px-1 text-[10px] font-semibold text-primary">
              {documents.length}
            </span>
          )}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-3 w-3 text-muted-foreground/60" />
        ) : (
          <ChevronDown className="h-3 w-3 text-muted-foreground/60" />
        )}
      </button>

      {isExpanded && (
        <div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => inputRef.current?.click()}
            className={`flex shrink-0 cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed px-3 py-3 text-center transition ${
              isDragging
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground'
            } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <Upload className="h-3.5 w-3.5" />
            <span className="text-[10px]">
              {isUploading ? 'Hochladen…' : 'Quelle hinzufügen'}
            </span>
            <span className="text-[9px] text-muted-foreground">{ACCEPTED_EXTENSIONS.join(', ')} · max. {MAX_FILE_SIZE_MB} MB</span>
          </div>

          {documents.length > 0 && (
            <div className="mt-2 flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
              {documents.map((doc) => (
                <DocumentListItem
                  key={doc.id}
                  document={doc}
                  onDelete={handleDelete}
                  isSelected={doc.id === selectedDocumentId}
                  onSelect={onSelectDocument}
                />
              ))}
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            accept={ACCEPTED_ACCEPT}
            onChange={handleFileInput}
            disabled={isUploading}
          />
        </div>
      )}

      {activeWorkspace && uploadTriageStatus !== 'idle' && (
        <ConnectorTriageBatchDialog
          open={triageDialogOpen}
          onOpenChange={(open) => {
            setTriageDialogOpen(open);
            // Read current status from store (not stale closure) to avoid
            // missing the 'confirmed' state set synchronously by submitUploadConfirmation.
            const currentStatus = useTriageStore.getState().status[uploadKey];
            if (!open && currentStatus === 'confirmed') {
              useTriageStore.getState().clearTriage(uploadKey);
            }
          }}
          connectorId={uploadKey}
          workspaceId={activeWorkspace.id}
          sourceType="upload"
          token={lastTokenRef.current}
          onUploadConfirmed={(documentIds) => {
            documentIds.forEach((documentId) => {
              setDocumentStatus(documentId, 'Parsing', 'Upload-Triage bestätigt - Ingest gestartet');
            });

            void getAccessToken().then((token) => {
              if (token) {
                return Promise.all(
                  documentIds.map((documentId) => fetchProcessingLogs(documentId, token)),
                );
              }
              return undefined;
            });
          }}
          onUploadRejected={async (documentIds) => {
            const token = await getAccessToken().catch(() => null);
            if (!token) {
              toast.error('Authentifizierung erforderlich');
              return;
            }

            await Promise.all(
              documentIds.map(async (documentId) => {
                try {
                  await deleteDocument(documentId, token);
                } catch {
                  toast.error('Abgelehntes Dokument konnte nicht gelöscht werden');
                }
              }),
            );

            if (activeWorkspace?.id) {
              await fetchDocuments(activeWorkspace.id, token);
            }
          }}
        />
      )}
    </div>
  );
}
